import { useState, useEffect, useCallback } from 'react';
import { ecommerceAPI, agentsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  ShoppingCart, Package, XCircle, Bot, Clock, Save, Loader2,
  Copy, Check, Download, ExternalLink, ShoppingBag, AlertCircle,
  Phone, FileText, Tag, Plus, Trash2, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const TRIGGERS = [
  { key: 'abandoned_cart', label: 'Abandoned Cart', icon: ShoppingCart, color: 'amber',
    desc: 'Call customers who added items but didn\'t complete purchase',
    defaultDelay: 30, supportsCoupons: true },
  { key: 'order_success', label: 'Order Success', icon: Package, color: 'emerald',
    desc: 'Call to confirm order, thank customer, or upsell',
    defaultDelay: 0 },
  { key: 'order_failed', label: 'Order Failed', icon: XCircle, color: 'red',
    desc: 'Call to inform about payment failure and help resolve',
    defaultDelay: 5 },
];

const colorMap = {
  amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-400', icon: 'text-amber-400' },
  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-400', icon: 'text-emerald-400' },
  red: { bg: 'bg-red-500/10', border: 'border-red-500/20', text: 'text-red-400', icon: 'text-red-400' },
};

function CouponsEditor({ coupons, onChange }) {
  const list = Array.isArray(coupons) ? coupons : [];
  const addCoupon = () => onChange([...list, { code: '', discount_percent: 10 }]);
  const removeCoupon = (idx) => onChange(list.filter((_, i) => i !== idx));
  const updateCoupon = (idx, patch) =>
    onChange(list.map((c, i) => (i === idx ? { ...c, ...patch } : c)));

  return (
    <div className="space-y-2" data-testid="coupons-editor">
      <div className="flex items-center justify-between">
        <label className="text-xs text-slate-300 flex items-center gap-1.5 font-medium">
          <Tag className="w-3.5 h-3.5 text-amber-400" /> Discount Coupons
        </label>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={addCoupon}
          data-testid="add-coupon-btn"
          className="h-7 px-2 text-xs border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
        >
          <Plus className="w-3 h-3 mr-1" /> Add Coupon
        </Button>
      </div>
      {list.length === 0 ? (
        <p className="text-[11px] text-slate-500 italic">
          No coupons configured. AI will only nudge to complete checkout — no discount offered.
        </p>
      ) : (
        <div className="space-y-2">
          {list.map((cp, i) => (
            <div key={i} className="grid grid-cols-[1fr_120px_36px] gap-2 items-center" data-testid={`coupon-row-${i}`}>
              <Input
                value={cp.code || ''}
                onChange={(e) => updateCoupon(i, { code: e.target.value.toUpperCase() })}
                placeholder="DIS10"
                data-testid={`coupon-code-${i}`}
                className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600 font-mono text-sm h-9"
              />
              <div className="relative">
                <Input
                  type="number"
                  min="1" max="99" step="1"
                  value={cp.discount_percent ?? ''}
                  onChange={(e) => updateCoupon(i, { discount_percent: parseFloat(e.target.value) || 0 })}
                  placeholder="10"
                  data-testid={`coupon-pct-${i}`}
                  className="bg-slate-950 border-white/10 text-white pr-7 h-9"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-500">%</span>
              </div>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => removeCoupon(i)}
                data-testid={`coupon-delete-${i}`}
                className="h-9 w-9 p-0 text-red-400 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TriggerCard({ trigger, config, agents, onChange }) {
  const c = colorMap[trigger.color];
  const tc = config || {
    enabled: false, agent_id: '', delay_minutes: trigger.defaultDelay,
    custom_script: '', coupons: [], ai_instruction: ''
  };

  return (
    <div className={`rounded-xl border ${tc.enabled ? c.border : 'border-white/5'} ${tc.enabled ? c.bg : 'bg-slate-900/30'} p-5 transition-all`}
      data-testid={`trigger-${trigger.key}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tc.enabled ? c.bg : 'bg-slate-800/50'}`}>
            <trigger.icon className={`w-5 h-5 ${tc.enabled ? c.icon : 'text-slate-500'}`} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white">{trigger.label}</h3>
            <p className="text-xs text-slate-500">{trigger.desc}</p>
          </div>
        </div>
        <Switch checked={tc.enabled}
          onCheckedChange={(v) => onChange({ ...tc, enabled: v })}
          data-testid={`toggle-${trigger.key}`} />
      </div>

      {tc.enabled && (
        <div className="space-y-3 mt-3 pt-3 border-t border-white/5 animate-fade-in">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">AI Agent</label>
              <Select value={tc.agent_id} onValueChange={(v) => onChange({ ...tc, agent_id: v })}>
                <SelectTrigger className="bg-slate-950 border-white/10 text-white text-sm" data-testid={`agent-${trigger.key}`}>
                  <SelectValue placeholder="Select agent..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10">
                  {agents.map(a => (
                    <SelectItem key={a.id} value={a.id} className="text-white hover:bg-white/5">
                      <div className="flex items-center gap-2"><Bot className="w-3 h-3" /> {a.name}</div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">
                <Clock className="w-3 h-3 inline mr-1" />Delay (minutes)
              </label>
              <Input type="number" min="0" value={tc.delay_minutes}
                onChange={(e) => onChange({ ...tc, delay_minutes: parseInt(e.target.value) || 0 })}
                data-testid={`delay-${trigger.key}`}
                className="bg-slate-950 border-white/10 text-white text-sm" />
              <p className="text-[10px] text-slate-600 mt-0.5">
                {tc.delay_minutes === 0 ? 'Call immediately' : `Call ${tc.delay_minutes} min after event`}
              </p>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Custom Script (optional)</label>
            <textarea value={tc.custom_script}
              onChange={(e) => onChange({ ...tc, custom_script: e.target.value })}
              placeholder={`What should the AI say for ${trigger.label.toLowerCase()}?`}
              rows={2}
              data-testid={`script-${trigger.key}`}
              className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-600 outline-none focus:ring-2 focus:ring-amber-500/40 resize-none" />
          </div>

          {trigger.supportsCoupons && (
            <div className="rounded-lg bg-slate-950/40 border border-amber-500/15 p-3 space-y-3" data-testid={`coupons-block-${trigger.key}`}>
              <div className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                <p className="text-[11px] text-slate-400 leading-relaxed">
                  When the customer agrees to a discount on the call, DotCall will <b className="text-amber-300">automatically WhatsApp</b> them
                  the cart link + the coupon code the AI offered. Requires WhatsApp Business set up under Integrations.
                </p>
              </div>
              <CouponsEditor
                coupons={tc.coupons || []}
                onChange={(coupons) => onChange({ ...tc, coupons })}
              />
              <div>
                <label className="text-xs text-slate-300 mb-1 block flex items-center gap-1.5 font-medium">
                  <Bot className="w-3.5 h-3.5 text-amber-400" /> AI Discount Logic Instruction
                </label>
                <textarea
                  value={tc.ai_instruction || ''}
                  onChange={(e) => onChange({ ...tc, ai_instruction: e.target.value })}
                  placeholder="e.g. If cart total is between ₹500 and ₹1000, offer DIS10 (10%). If cart is above ₹1000, offer HUGE25 (25%). Never invent codes. Mention the code clearly."
                  rows={3}
                  data-testid={`ai-instruction-${trigger.key}`}
                  className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-600 outline-none focus:ring-2 focus:ring-amber-500/40 resize-none"
                />
                <p className="text-[10px] text-slate-600 mt-0.5">
                  This instruction is injected verbatim into the AI's prompt. Be specific about cart-value ranges and which coupon to offer.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function EcommercePage() {
  const [config, setConfig] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState([]);
  const [tab, setTab] = useState('setup');
  const [copied, setCopied] = useState('');
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, agRes, logRes] = await Promise.all([
        ecommerceAPI.getConfig(), agentsAPI.list(), ecommerceAPI.getLogs()
      ]);
      setConfig(cfgRes.data);
      setAgents(agRes.data || []);
      setLogs(logRes.data || []);
    } catch { toast.error('Failed to load config'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleTriggerChange = (key, val) => {
    setConfig(prev => ({ ...prev, [key]: val }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      TRIGGERS.forEach(t => { if (config[t.key]) payload[t.key] = config[t.key]; });
      await ecommerceAPI.updateConfig(payload);
      toast.success('E-commerce triggers saved');
      setDirty(false);
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(''), 2000);
  };

  const handleDownloadPlugin = async () => {
    try {
      const res = await ecommerceAPI.downloadPlugin();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'dotcall-woocommerce.php';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Plugin downloaded');
    } catch { toast.error('Download failed'); }
  };

  if (loading) return (
    <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>
  );

  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
  const webhookShopify = `${baseUrl}/api/webhooks/shopify/${config?.user_id}?key=${config?.webhook_key}`;
  const webhookWoo = `${baseUrl}/api/webhooks/woocommerce/${config?.user_id}?key=${config?.webhook_key}`;

  const logStatusColors = {
    called: 'text-emerald-400', scheduled: 'text-blue-400', triggering: 'text-amber-400',
    skipped: 'text-slate-400', failed: 'text-red-400',
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="ecommerce-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit'] text-white">E-Commerce</h1>
          <p className="text-slate-400 mt-1">Auto-trigger AI calls on Shopify & WooCommerce events</p>
        </div>
        {dirty && (
          <Button onClick={handleSave} disabled={saving} data-testid="save-ecom-btn"
            className="bg-amber-600 hover:bg-amber-700 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-900/50 rounded-xl p-1 border border-white/5 w-fit" data-testid="ecom-tabs">
        {[
          { id: 'setup', label: 'Triggers', icon: Bot },
          { id: 'connect', label: 'Connect', icon: ExternalLink },
          { id: 'logs', label: 'Logs', icon: FileText },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              tab === t.id ? 'bg-amber-600/15 text-amber-300' : 'text-slate-400 hover:text-white hover:bg-white/5'
            }`}>
            <t.icon className="w-4 h-4" /> {t.label}
            {t.id === 'logs' && logs.length > 0 && <Badge className="bg-amber-600/20 text-amber-300 border-amber-600/30 text-xs">{logs.length}</Badge>}
          </button>
        ))}
      </div>

      {/* Setup Tab */}
      {tab === 'setup' && config && (
        <div className="space-y-4" data-testid="setup-tab">
          {agents.length === 0 && (
            <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-300">Create an AI Agent first to assign it to triggers. Go to <a href="/dashboard/agents" className="underline">AI Agents</a>.</p>
            </div>
          )}
          {TRIGGERS.map(t => (
            <TriggerCard key={t.key} trigger={t} config={config[t.key]}
              agents={agents} onChange={(val) => handleTriggerChange(t.key, val)} />
          ))}
        </div>
      )}

      {/* Connect Tab */}
      {tab === 'connect' && config && (
        <div className="space-y-6" data-testid="connect-tab">
          {/* Shopify */}
          <div className="glass-card rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white font-['Outfit']">Shopify</h3>
                <p className="text-xs text-slate-400">Connect via Shopify Webhooks</p>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Webhook URL</label>
                <div className="flex gap-2">
                  <Input readOnly value={webhookShopify} className="bg-slate-950 border-white/10 text-white text-xs font-mono flex-1" data-testid="shopify-webhook-url" />
                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(webhookShopify, 'shopify')}
                    className="text-slate-400 hover:text-white">
                    {copied === 'shopify' ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <div className="p-4 rounded-lg bg-slate-800/50 text-xs text-slate-300 space-y-2">
                <p className="font-medium text-white">Setup Instructions:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Go to <b>Shopify Admin → Settings → Notifications → Webhooks</b></li>
                  <li>Click "Create webhook" and add for these events:</li>
                  <li className="ml-4">• <code className="text-amber-400">orders/create</code> (Order Success)</li>
                  <li className="ml-4">• <code className="text-amber-400">orders/cancelled</code> (Order Failed)</li>
                  <li className="ml-4">• <code className="text-amber-400">checkouts/create</code> (Abandoned Cart)</li>
                  <li>Set format to <b>JSON</b> and paste the webhook URL above</li>
                  <li>For abandoned checkouts: use <b>Shopify Flow</b> → "Checkout abandoned" trigger → "Send HTTP request" with the URL above</li>
                </ol>
              </div>
            </div>
          </div>

          {/* WooCommerce */}
          <div className="glass-card rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white font-['Outfit']">WooCommerce</h3>
                <p className="text-xs text-slate-400">Install the DotCall WordPress plugin</p>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Webhook URL</label>
                <div className="flex gap-2">
                  <Input readOnly value={webhookWoo} className="bg-slate-950 border-white/10 text-white text-xs font-mono flex-1" data-testid="woo-webhook-url" />
                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(webhookWoo, 'woo')}
                    className="text-slate-400 hover:text-white">
                    {copied === 'woo' ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <Button onClick={handleDownloadPlugin} data-testid="download-woo-plugin"
                className="bg-amber-600 hover:bg-amber-700 text-white text-sm">
                <Download className="w-4 h-4 mr-2" /> Download WooCommerce Plugin
              </Button>
              <div className="p-4 rounded-lg bg-slate-800/50 text-xs text-slate-300 space-y-2">
                <p className="font-medium text-white">Setup Instructions:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Download the plugin file above</li>
                  <li>Go to <b>WordPress Admin → Plugins → Add New → Upload Plugin</b></li>
                  <li>Upload the <code className="text-amber-400">dotcall-woocommerce.php</code> file and activate it</li>
                  <li>Done! The plugin auto-triggers calls for order events and checks for abandoned carts hourly</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Webhook Key */}
          <div className="glass-card rounded-xl p-5">
            <h4 className="text-sm font-semibold text-white mb-3">Your Webhook Key</h4>
            <div className="flex gap-2">
              <Input readOnly value={config.webhook_key} className="bg-slate-950 border-white/10 text-white text-xs font-mono flex-1" data-testid="webhook-key" />
              <Button size="sm" variant="ghost" onClick={() => copyToClipboard(config.webhook_key, 'key')}
                className="text-slate-400 hover:text-white">
                {copied === 'key' ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-[10px] text-slate-500 mt-1">This key authenticates webhook requests. Keep it secret.</p>
          </div>
        </div>
      )}

      {/* Logs Tab */}
      {tab === 'logs' && (
        <div className="space-y-3" data-testid="logs-tab">
          {logs.length === 0 ? (
            <div className="glass-card rounded-xl p-12 text-center">
              <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold font-['Outfit'] text-white mb-2">No webhook events yet</h3>
              <p className="text-slate-400 text-sm">Events will appear here once your Shopify/WooCommerce sends webhooks</p>
            </div>
          ) : (
            <div className="rounded-xl border border-white/10 bg-slate-900/50 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="px-4 py-3 text-xs text-slate-400 font-medium text-left">Time</th>
                    <th className="px-4 py-3 text-xs text-slate-400 font-medium text-left">Event</th>
                    <th className="px-4 py-3 text-xs text-slate-400 font-medium text-left">Source</th>
                    <th className="px-4 py-3 text-xs text-slate-400 font-medium text-left">Customer</th>
                    <th className="px-4 py-3 text-xs text-slate-400 font-medium text-left">Order</th>
                    <th className="px-4 py-3 text-xs text-slate-400 font-medium text-left">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {logs.map(log => (
                    <tr key={log.id} className="hover:bg-white/[0.02]" data-testid={`log-${log.id}`}>
                      <td className="px-4 py-3 text-slate-400 text-xs">{new Date(log.created_at).toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3">
                        <Badge className={`text-xs ${
                          log.event_type === 'abandoned_cart' ? 'bg-amber-600/20 text-amber-400' :
                          log.event_type === 'order_success' ? 'bg-emerald-600/20 text-emerald-400' :
                          'bg-red-600/20 text-red-400'
                        }`}>{log.event_type?.replace('_', ' ')}</Badge>
                      </td>
                      <td className="px-4 py-3 text-slate-300 text-xs capitalize">{log.source}</td>
                      <td className="px-4 py-3 text-white text-xs">{log.customer?.name || '—'}<br/><span className="text-slate-500">{log.customer?.phone}</span></td>
                      <td className="px-4 py-3 text-slate-300 text-xs">{log.order?.order_name || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium ${logStatusColors[log.status] || 'text-slate-400'}`}>
                          {log.status}
                        </span>
                        {log.error && <p className="text-[10px] text-red-400 mt-0.5">{log.error}</p>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
